import React, {useCallback, useEffect, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Paper, Typography} from "@mui/material";
import Button from "../../../../../components/base/button/button";
import {Col} from "reactstrap";
import AddressCardV2 from "./address-card";
import UpsertCustomerAddressDialogV2 from "./upsert-address-dialog-v2";
import Api from "../../../../../../core/services/api/api";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {useParams} from "react-router-dom";
import {useAppSelector} from "../../../../../redux";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const initUpsertAddressDialogState = {open: false, address: undefined, editing: false, duplicating: false};

const allowCreation = false;
const allowRemoval = false;
const allowSetDefault = false;

const CustomerAddressesV2View = () => {
    const params = useParams();
    const [loading, setLoading] = useState(true);
    const [settingAsDefault, setSettingAsDefault] = useState({from: null, to: null});
    const [upsertAddressDialogState, setUpsertAddressDialogState] = useState(initUpsertAddressDialogState);
    const [billingAddress, setBillingAddress] = useState(undefined);
    const [shippingAddresses, setShippingAddresses] = useState([]);
    const isMounted = useIsMounted();

    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('customer-addresses-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     */
    useEffect(() => {
        if (!defaultCustomer?.code)
            return;
        configureNotebookTags((noteTags) => noteTags.customerAddresses(defaultCustomer.code));
        configureBehavioralTool((tags) => tags.customerAddresses(defaultCustomer.code));
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer.code]);

    /**
     * Fetches the addresses of the customer from the server.
     * @type {() => Promise<void>}
     */
    const getCustomerAddresses = useCallback(async (showLoading = true) => {
        if (showLoading) {
            setLoading(true);
        }
        const response = await Api.getCustomerAddressesV2(params.customerId);
        if (!isMounted())
            return;
        if (response?.resultFlag && response.data) {
            setBillingAddress(response.data.billingAddress);
            setShippingAddresses(response.data.shippingAddresses ?? []);
        } else {
            setBillingAddress(undefined);
            setShippingAddresses([]);
        }
        if (showLoading) {
            setLoading(false);
        }
    }, [params.customerId, isMounted])


    /**
     * As soon as the component is mounted, fetches the addresses of the user.
     */
    useEffect(() => {
        void getCustomerAddresses();
    }, [getCustomerAddresses])

    /**
     * Creates a new address with the given values by calling the applicable API.
     * @type {(function(*): Promise<boolean|*>)|*}
     */
    const createAddress = useCallback(async (address) => {
        const forApi = {
            ...address,
            customerId: Number(params.customerId),
        }
        const response = await Api.createCustomerAddressV2(forApi);
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            void getCustomerAddresses(false);
        }
        return response?.resultFlag ?? false;
    }, [params.customerId, isMounted, getCustomerAddresses])

    /**
     * Edits the address with the given values.
     * @type {(address: any) => Promise<boolean>}
     */
    const editAddress = useCallback(async (address) => {
        const forApi = {
            ...address,
            customerId: Number(params.customerId),
            addressId: address.id,
        }
        const response = await Api.updateCustomerAddressV2(forApi);
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            void getCustomerAddresses(false);
        }
        return response?.resultFlag ?? false;
    }, [params.customerId, isMounted, getCustomerAddresses])


    /**
     * Sets the given address as the default address by calling the applicable API.
     * @type {(function(*): Promise<boolean|*>)}
     */
    const setAddressAsDefault = useCallback(async (address) => {
        const currentDefaultAddressId = billingAddress?.isDefault ? billingAddress.id : shippingAddresses?.find(sa => sa.isDefault)?.id;
        setSettingAsDefault({from: currentDefaultAddressId, to: address.id});
        const response = await Api.setDefaultCustomerAddressV2({
            customerId: Number(params.customerId),
            addressId: address.id,
        });
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            await getCustomerAddresses(false);
        }
        if (!isMounted())
            return false;
        setSettingAsDefault({from: null, to: null});
        return response?.resultFlag ?? false;
    }, [params.customerId, isMounted, getCustomerAddresses, billingAddress, shippingAddresses]);

    /**
     * Removes the given address by calling the applicable API.
     * @type {(function(*): Promise<boolean|*>)|*}
     */
    const removeAddress = useCallback(async (address) => {
        const response = await Api.deleteCustomerAddressV2(Number(params.customerId), address.id);
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            void getCustomerAddresses(false);
        }
        return response?.resultFlag ?? false;
    }, [params.customerId, isMounted, getCustomerAddresses])

    /**
     * Updates the taxes of the given address by calling the applicable API.
     * @type {(function(*, *): Promise<boolean|*>)|*}
     */
    const updateAddressTaxes = useCallback(async (address, taxes) => {
        const forApi = {
            customerId: Number(params.customerId),
            addressId: address.id,
            salesTaxes: taxes,
        }
        const response = await Api.updateCustomerAddressTaxV2(forApi);
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            void getCustomerAddresses(false);
        }
        return response?.resultFlag ?? false;
    }, [params.customerId, isMounted, getCustomerAddresses])


    /**
     * Invokes the [editAddress] callback to upsert an address.
     * Creation is currently not supported.
     * @type {(values: any) => Promise<boolean>}
     */
    const upsertAddress = useCallback(async (values) => {
        if (upsertAddressDialogState.editing) {
            const address = {
                ...values,
                id: upsertAddressDialogState.address.id,
            }
            return await editAddress(address);
        }
        return createAddress(values);
    }, [createAddress, editAddress, upsertAddressDialogState.address?.id, upsertAddressDialogState.editing])

    /**
     * Opens the upsert address dialog with the given address.
     *
     * * if duplicating, the mode of the address is 'create'
     * * if address exists, the mode of the address is 'edit'
     * * in other cases, the mode of the address is 'create'
     * @type {(typeStr: string, address?: any, duplicating?: boolean) => void}
     */
    const openUpsertAddressDialog = useCallback((typeStr, address = undefined, duplicating = false) => {
        setUpsertAddressDialogState({
            open: true,
            address: {
                ...(address ?? {}),
                type: typeStr,
            },
            editing: !!address && !duplicating,
            duplicating: duplicating,
        });
    }, [])

    return (
        <>
            <Typography variant={'h6'} fontWeight={600} className={'mb-4'}>
                {defaultCustomer?.name}'s Addresses
            </Typography>
            <Paper className={'paper w-100 p-3 mt-4 d-flex flex-column'}>
                <div className={'d-flex align-items-center justify-content-between mb-3 w-100'}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Shipping Addresses
                    </Typography>
                    {
                        allowCreation &&
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => openUpsertAddressDialog('shipping')}
                        >
                            Add Address
                        </Button>
                    }
                </div>
                <div className={'address-cards-container'}>
                    {
                        loading &&
                        Array(6).fill(null).map((_, e) => (
                            <Col key={e} xs={12} md={6} xl={4} className={'loading extraLarge mb-4'}>
                                <div/>
                            </Col>
                        ))
                    }
                    {
                        !loading &&
                        shippingAddresses?.map((address, _, array) => (
                            <AddressCardV2
                                key={address.id}
                                address={address}
                                editTaxes={(taxes) => updateAddressTaxes(address, taxes)}
                                edit={() => openUpsertAddressDialog('shipping', address)}
                                duplicate={() => openUpsertAddressDialog('shipping', address, true)}
                                setDefault={() => setAddressAsDefault(address)}
                                remove={() => removeAddress(address)}
                                allowCreation={allowCreation}
                                allowSetDefault={allowSetDefault}
                                allowRemoval={allowRemoval && array.length > 1}
                                settingAsDefault={settingAsDefault.to === address.id || settingAsDefault.from === address.id}
                            />
                        ))
                    }
                    {
                        !loading && shippingAddresses?.length === 0 &&
                        <div className={'d-flex justify-content-center w-100 my-5 py-5'}>
                            <Typography variant={'body2'} color={'textSecondary'}>
                                No Shipping Addresses found for this customer.
                            </Typography>
                        </div>
                    }
                </div>
                <div className={'d-flex align-items-center justify-content-between mb-3 mt-4 w-100'}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Billing Address
                    </Typography>
                    {
                        allowCreation && !billingAddress &&
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            onClick={() => openUpsertAddressDialog('billing')}
                        >
                            Add Address
                        </Button>
                    }
                </div>
                <div className={'address-cards-container'}>
                    {
                        loading &&
                        Array(6).fill(null).map((_, e) => (
                            <Col key={e} xs={12} md={6} xl={4} className={'loading extraLarge mb-4'}>
                                <div/>
                            </Col>
                        ))
                    }
                    {
                        !loading && billingAddress &&
                        <AddressCardV2
                            address={billingAddress}
                            editTaxes={(taxes) => updateAddressTaxes(billingAddress, taxes)}
                            edit={() => openUpsertAddressDialog('billing', billingAddress)}
                            duplicate={() => openUpsertAddressDialog('shipping', billingAddress, true)}
                            setDefault={() => setAddressAsDefault(billingAddress)}
                            remove={() => removeAddress(billingAddress)}
                            allowCreation={allowCreation}
                            allowSetDefault={allowSetDefault}
                            allowRemoval={allowRemoval && false}
                            settingAsDefault={settingAsDefault.to === billingAddress.id || settingAsDefault.from === billingAddress.id}
                        />
                    }
                    {
                        !loading && !billingAddress &&
                        <div className={'d-flex justify-content-center w-100 my-5 py-5'}>
                            <Typography variant={'body2'} color={'textSecondary'}>
                                No Billing Address found for this customer.
                            </Typography>
                        </div>
                    }
                </div>
            </Paper>
            <UpsertCustomerAddressDialogV2
                open={upsertAddressDialogState.open}
                address={upsertAddressDialogState.address}
                editing={upsertAddressDialogState.editing}
                duplicating={upsertAddressDialogState.duplicating}
                hasBillingAddress={!!billingAddress}
                close={() => setUpsertAddressDialogState(initUpsertAddressDialogState)}
                submit={upsertAddress}
            />
        </>
    );
}

export default CustomerAddressesV2View;
